
import { useState } from "react";
import { toast } from "sonner";
import { 
  EnhancedColorSuggestion, 
  EnhancedColorResult, 
  ColorFeedback 
} from "./color-check/types";
import { analyzeColorWithAI } from "./color-check/colorAnalysis";
import { fetchAISuggestions } from "./color-check/suggestionService";
import { submitFeedback } from "./color-check/feedbackService";
import { formatTimestamp } from "./color-check/utils";

export const useEnhancedColorCheck = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<EnhancedColorSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [colorResult, setColorResult] = useState<EnhancedColorResult | null>(null);
  const [recentChecks, setRecentChecks] = useState<EnhancedColorResult[]>([]);
  const [isLearning, setIsLearning] = useState(false);

  // Enhanced search with AI-powered suggestions
  const handleSearchTermChange = async (term: string) => {
    setSearchTerm(term);
    
    const aiSuggestions = await fetchAISuggestions(term);
    setSuggestions(aiSuggestions);
    setShowSuggestions(aiSuggestions.length > 0);
  };
  
  const handleSelectSuggestion = (suggestion: EnhancedColorSuggestion) => {
    setSearchTerm(suggestion.label);
    setShowSuggestions(false);
    analyzeColor(suggestion.value);
  };
  
  // Enhanced color analysis with proper color prioritization
  const analyzeColor = async (inputValue?: string) => {
    console.log("🔍 Enhanced analyzeColor called with:", { inputValue, searchTerm, inputType: typeof inputValue });
    
    // Properly determine the value to analyze
    let valueToAnalyze: string;
    
    if (typeof inputValue === 'string' && inputValue.trim()) {
      valueToAnalyze = inputValue.trim();
    } else {
      valueToAnalyze = searchTerm?.trim() || '';
    }
    
    console.log("📝 Final value to analyze:", valueToAnalyze);
    
    if (!valueToAnalyze) {
      toast.error("Vul een kleurcode en product in om te analyseren");
      return;
    }

    setIsLoading(true);
    
    try {
      const result = await analyzeColorWithAI(valueToAnalyze);
      
      if (result) {
        setColorResult(result);
        setRecentChecks(prev => [result, ...prev].slice(0, 10));
      }
      
    } finally {
      setIsLoading(false);
    }
  };

  // Provide learning feedback to improve AI
  const provideFeedback = async (feedback: ColorFeedback) => {
    setIsLearning(true);
    
    try {
      await submitFeedback(feedback, colorResult);
    } finally {
      setIsLearning(false);
    }
  };

  const sendExternalRequest = async () => {
    if (!colorResult) return;
    
    toast.info("Externe aanvraag wordt verstuurd...");
    
    try {
      setTimeout(() => {
        toast.success("Externe aanvraag succesvol verstuurd", {
          description: `Aanvraag voor ${colorResult.parsedInfo.colorCode} is doorgestuurd naar leveranciers`
        });
      }, 1500);
    } catch (error) {
      toast.error("Externe aanvraag gefaald");
    }
  };

  return {
    searchTerm,
    suggestions,
    showSuggestions,
    isLoading,
    isLearning,
    colorResult,
    recentChecks,
    handleSearchTermChange,
    handleSelectSuggestion,
    analyzeColor,
    provideFeedback,
    sendExternalRequest,
    formatTimestamp
  };
};

// Re-export types for backward compatibility
export type {
  EnhancedColorSuggestion,
  ParsedColorInfo,
  BaseColorPrediction,
  InventoryMatch,
  EnhancedColorResult,
  ColorFeedback
} from "./color-check/types";
